import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Stack, type SvgIconProps, Typography } from "@mui/material";
import {
  LICENSE_CODE_TO_DATA_MAP,
  getLicenseImage,
} from "Components/Licenses/licenseUtils";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { FullHeightForm } from "Components/Shared/FormStyles";
import {
  LicensePaymentStatus,
  type PartyLicenseDto,
} from "Infrastructure/Api/Api";
import type { ComponentType } from "react";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Onboarding;

type PaidStatusDisplay = {
  Icon: ComponentType<SvgIconProps>;
  color: SvgIconProps["color"];
  message: string;
};

// The statuses `getLicenseResumeState` reports as "paid" — anything else has no business here.
const PAID_STATUS_DISPLAY: Partial<
  Record<LicensePaymentStatus, PaidStatusDisplay>
> = {
  [LicensePaymentStatus.Processing]: {
    Icon: AccountBalanceIcon,
    color: "warning",
    message: COMPONENT_RESOURCES.LicensePaymentProcessing,
  },
  [LicensePaymentStatus.Success]: {
    Icon: CheckCircleOutlineIcon,
    color: "success",
    message: COMPONENT_RESOURCES.LicensePaymentReceived,
  },
};

type Props = {
  license: PartyLicenseDto;
  onContinue: () => void;
  isSubmitting: boolean;
};

export const PaidLicenseStepContent = ({
  license: { code, region, licenseNo, paymentStatus },
  onContinue,
  isSubmitting,
}: Props) => {
  const { t } = useResource();
  const { title } = LICENSE_CODE_TO_DATA_MAP[code];
  const display = PAID_STATUS_DISPLAY[paymentStatus];

  if (!display) {
    return <ErrorText>{t(Resources.Errors.RequestError)}</ErrorText>;
  }

  const { Icon, color, message } = display;

  return (
    <FullHeightForm>
      <StepBox
        submitButtonLabel={t(Resources.Common.Continue)}
        onSubmitClick={onContinue}
        isSubmitting={isSubmitting}
      >
        <Stack
          height="100%"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          gap={5}
        >
          <Stack alignItems="center" gap={2}>
            <Box
              component="img"
              src={getLicenseImage(code, region)}
              alt={title}
              sx={{
                height: 280,
                borderRadius: "10px",
                boxShadow: "0px 0px 12px 0px #00000029",
              }}
            />
            <Stack>
              <Typography fontWeight={600} fontSize={18}>
                {title}
              </Typography>
              {licenseNo && (
                <Typography color="text.secondary">{`#${licenseNo}`}</Typography>
              )}
            </Stack>
          </Stack>
          <Stack alignItems="center" gap={1}>
            <Icon color={color} sx={{ fontSize: 64 }} />
            <Typography variant="h6">{t(message)}</Typography>
          </Stack>
        </Stack>
      </StepBox>
    </FullHeightForm>
  );
};
